// AmbienceManager — two-slot crossfade engine for biome ambient loops

import { AMBIENCE_MANIFEST } from './ambienceManifest.mjs';
import {
  EVAL_THROTTLE_MS,
  SWITCH_HOLD_MS,
  HYSTERESIS,
  MIN_CONFIDENCE_TO_SWITCH,
  DEFAULT_FADE_SEC,
} from './constants.mjs';

/**
 * @typedef {{ audioEl: HTMLAudioElement, sourceNode: MediaElementAudioSourceNode, gainNode: GainNode, biome: string|null }} AmbienceSlot
 */

export class AmbienceManager {
  /**
   * @param {AudioManager} audioManager — must expose getAmbienceBus()
   */
  constructor(audioManager) {
    this._audioManager = audioManager;
    this._slots = [null, null]; // AmbienceSlot[]
    this._activeSlotIndex = 0;
    this._currentBiome = null;
    this._enabled = true;

    // Throttle / hold state
    this._lastEvalMs = 0;
    this._lastSwitchMs = 0;

    // Audio graph refs (set in unlock)
    this._audioContext = null;
    this._busGain = null;
  }

  /** Create audio elements and connect to the ambience bus. Call after AudioManager.unlock(). */
  unlock() {
    const bus = this._audioManager.getAmbienceBus();
    if (!bus) return;
    this._audioContext = bus.audioContext;
    this._busGain = bus.ambienceGain;

    for (let i = 0; i < 2; i++) {
      const audioEl = new Audio();
      audioEl.loop = true;
      audioEl.preload = 'none';
      // Needed so MediaElementSource works
      audioEl.crossOrigin = 'anonymous';

      const sourceNode = this._audioContext.createMediaElementSource(audioEl);
      const gainNode = this._audioContext.createGain();
      gainNode.gain.value = 0;

      sourceNode.connect(gainNode);
      gainNode.connect(this._busGain);

      this._slots[i] = { audioEl, sourceNode, gainNode, biome: null };
    }
  }

  /** Whether the manager has been unlocked and is ready */
  get isReady() {
    return this._slots[0] !== null;
  }

  /** Enable/disable ambience playback */
  setEnabled(enabled) {
    this._enabled = enabled;
    if (!enabled) {
      this._fadeOutAll();
    } else if (this._currentBiome) {
      // Resume current biome
      const slot = this._slots[this._activeSlotIndex];
      if (slot && slot.biome === this._currentBiome) {
        this._rampGain(slot.gainNode, 1, DEFAULT_FADE_SEC);
        slot.audioEl.play().catch(() => {});
      }
    }
  }

  get enabled() {
    return this._enabled;
  }

  /**
   * Throttled biome evaluation — call on mousemove/wheel/keyPan.
   * @param {{ targetBiome: string|null, confidence: number }} resolved
   */
  updateFromResolvedBiome(resolved) {
    if (!this.isReady || !this._enabled) return;

    const now = performance.now();
    if (now - this._lastEvalMs < EVAL_THROTTLE_MS) return;
    this._lastEvalMs = now;

    this._evaluateSwitch(resolved, now);
  }

  /**
   * Force evaluation — bypasses throttle but respects hold.
   * Call on pan-end / initial unlock.
   * @param {{ targetBiome: string|null, confidence: number }} resolved
   */
  forceUpdateFromResolvedBiome(resolved) {
    if (!this.isReady || !this._enabled) return;
    const now = performance.now();
    this._lastEvalMs = now;
    this._evaluateSwitch(resolved, now);
  }

  /** Called when volume settings change */
  onSettingsChanged() {
    // The bus gain is controlled by AudioManager._applyVolumes,
    // so nothing extra needed here. This hook exists for future use
    // (e.g. if we add per-ambience gain separate from the bus).
  }

  // ─── Internal ─────────────────────────────────────────

  _evaluateSwitch(resolved, now) {
    const { targetBiome, confidence } = resolved;

    if (!targetBiome) return;

    // Same biome — no switch needed
    if (targetBiome === this._currentBiome) return;

    // Hold check — don't switch too rapidly after a recent switch
    if (now - this._lastSwitchMs < SWITCH_HOLD_MS) return;

    // Confidence must meet minimum threshold
    if (confidence < MIN_CONFIDENCE_TO_SWITCH) return;

    // Hysteresis — new biome must clearly dominate
    // (For the first biome when _currentBiome is null, skip hysteresis)
    if (this._currentBiome !== null) {
      const currentScore = resolved.scores[this._currentBiome] || 0;
      if (confidence - currentScore < HYSTERESIS) return;
    }

    this._transitionToBiome(targetBiome);
    this._lastSwitchMs = now;
  }

  async _transitionToBiome(biome) {
    const prevIndex = this._activeSlotIndex;
    const nextIndex = 1 - prevIndex;

    const prevSlot = this._slots[prevIndex];
    const nextSlot = this._slots[nextIndex];

    // Fade out previous
    if (prevSlot && prevSlot.biome) {
      this._rampGain(prevSlot.gainNode, 0, DEFAULT_FADE_SEC);
      // Stop after fade completes
      const el = prevSlot.audioEl;
      setTimeout(() => {
        if (this._slots[prevIndex].biome !== biome) {
          el.pause();
        }
      }, DEFAULT_FADE_SEC * 1000 + 100);
    }

    // Load new biome into next slot if needed
    const url = AMBIENCE_MANIFEST[biome];
    if (!url) return;

    if (nextSlot.biome !== biome) {
      nextSlot.biome = biome;
      nextSlot.audioEl.src = url;

      // Wait for enough data to play
      await new Promise((resolve) => {
        const onCanPlay = () => {
          nextSlot.audioEl.removeEventListener('canplay', onCanPlay);
          resolve();
        };
        nextSlot.audioEl.addEventListener('canplay', onCanPlay);
        nextSlot.audioEl.load();
      });
    }

    // Start playing and fade in
    nextSlot.gainNode.gain.setValueAtTime(0, this._audioContext.currentTime);
    nextSlot.audioEl.play().catch(() => {});
    this._rampGain(nextSlot.gainNode, 1, DEFAULT_FADE_SEC);

    this._activeSlotIndex = nextIndex;
    this._currentBiome = biome;
  }

  _rampGain(gainNode, target, durationSec) {
    const now = this._audioContext.currentTime;
    gainNode.gain.cancelScheduledValues(now);
    gainNode.gain.setValueAtTime(gainNode.gain.value, now);
    gainNode.gain.linearRampToValueAtTime(target, now + durationSec);
  }

  _fadeOutAll() {
    for (const slot of this._slots) {
      if (slot && slot.biome) {
        this._rampGain(slot.gainNode, 0, 0.5);
        const el = slot.audioEl;
        setTimeout(() => el.pause(), 600);
      }
    }
  }
}
